import React from 'react';
import { Alert, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell } from '../grid';
import {
  HistoricalFactRow,
  HistoricalImportRow,
} from './bieHistorical.models';

type Props = {
  imports: HistoricalImportRow[];
  facts: HistoricalFactRow[];
  loadingImports?: boolean;
  loadingFacts?: boolean;
  selectedImport?: HistoricalImportRow | null;
  onSelectImport: (row: HistoricalImportRow | null) => void;
};

class HistoricalImportMasterDetail extends React.PureComponent<Props> {
  importColumns: GridColDef[] = [
    { field: 'sourcecode', headerName: 'Source', width: 120 },
    { field: 'scenariocode', headerName: 'Scenario', width: 140 },
    { field: 'filename', headerName: 'File', flex: 1, minWidth: 250 },
    { field: 'recordcount', headerName: 'Records', width: 100, type: 'number' },
    { field: 'validcount', headerName: 'Valid', width: 90, type: 'number' },
    { field: 'errorcount', headerName: 'Errors', width: 90, type: 'number' },
    { field: 'statename', headerName: 'Status', width: 120 },
    { field: 'importdate', headerName: 'Imported', width: 165 },
  ];

  factColumns: GridColDef[] = [
    { field: 'metriccode', headerName: 'Variable', width: 170 },
    { field: 'productcode', headerName: 'Product', width: 130 },
    { field: 'productname', headerName: 'Product Name', flex: 1, minWidth: 210 },
    { field: 'periodcode', headerName: 'Period', width: 120 },
    { field: 'quantity', headerName: 'Quantity', width: 120, type: 'number' },
    { field: 'unitprice', headerName: 'Unit Price', width: 130, renderCell: FGridMoneyCell },
    { field: 'amount', headerName: 'Amount', width: 160, renderCell: FGridMoneyCell },
    { field: 'statename', headerName: 'Status', width: 110 },
  ];

  render() {
    return (
      <Stack spacing={2}>
        <FDataGrid
          title="Historical Imports"
          subtitle="Select an import to review the facts created from it."
          rows={this.props.imports}
          columns={this.importColumns}
          loading={this.props.loadingImports}
          height={360}
          masterDetail
          showSelectionControl
          selectedRowId={this.props.selectedImport?.rid ?? null}
          onSelectedRowChange={(row) =>
            this.props.onSelectImport(row as HistoricalImportRow | null)
          }
          getRowId={(r:any) => r.rid}
          showQuickFilter
          pageSizeOptions={[10,25,50]}
          emptyState={{
            title: 'No Historical Imports',
            message: 'Manual and workbook imports will appear here.',
          }}
        />

        {!this.props.selectedImport ? (
          <Alert severity="info">
            Select an import above to display its historical facts.
          </Alert>
        ) : (
          <FDataGrid
            title={`Facts — ${this.props.selectedImport.filename}`}
            subtitle={`${this.props.selectedImport.recordcount} record(s) reported by the import.`}
            rows={this.props.facts}
            columns={this.factColumns}
            loading={this.props.loadingFacts}
            height={430}
            getRowId={(r:any) => r.rid}
            showQuickFilter
            pageSizeOptions={[25,50,100]}
            emptyState={{
              title: 'No Facts Found',
              message: 'No fact rows are linked to the selected import.',
            }}
          />
        )}
      </Stack>
    );
  }
}

export default HistoricalImportMasterDetail;
